// Campaign types shared across backend, admin panel, and advertiser portal —
// schemas + inferred types. The schema is authoritative — the TS type is
// derived via z.infer<> (shape unchanged from the pre-migration API).

import { z } from 'zod';

export const CAMPAIGN_STATUSES = [
  'draft',
  'active',
  'paused',
  'completed',
  /**
   * Soft-deleted by the advertiser. Archived campaigns:
   *   - are hidden from the default advertiser dashboard list
   *   - stop serving ads (the ad-serve query filters on status)
   *   - preserve historical billing / impressions / click data for audit
   * Hard delete is admin-only.
   */
  'archived',
] as const;

export const CampaignStatusSchema = z.enum(CAMPAIGN_STATUSES);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

// Why an auto-pause fired, surfaced in the campaign-paused email and by the billing
// service that triggers it (billingService.ts).
export const CAMPAIGN_PAUSE_REASONS = ['insufficient_balance', 'budget_reached'] as const;
export const CampaignPauseReasonSchema = z.enum(CAMPAIGN_PAUSE_REASONS);
export type CampaignPauseReason = z.infer<typeof CampaignPauseReasonSchema>;

export const CampaignSchema = z.object({
  id: z.string(),
  advertiserId: z.string(), // FK to advertiser_accounts
  name: z.string(),
  /** Optional long-form description, surfaced in the advertiser portal. */
  description: z.string().optional(),
  startDate: z.string(),
  endDate: z.string(),
  status: CampaignStatusSchema,
  globalFrequency: z.number().optional(),
  /**
   * Campaign budget in **whole US dollars** (a plain dollar amount, e.g. 500 = $500) —
   * NOT cents. Unlike the `*Cents` money fields elsewhere (balanceCents, spendCents,
   * cpmCents, …), do not compare `budget` against a `*Cents` value without converting.
   * (Moving budget to integer cents is a tracked follow-up.)
   */
  budget: z.number().optional(),
  /**
   * Default targeting at the campaign level. Individual ads can still
   * override or narrow this via their own targeting block — campaign
   * targeting acts as the inherited default.
   */
  targetSpecialties: z.array(z.string()).optional(),
  /**
   * Why the billing service auto-paused this campaign. Set with the pause, cleared whenever the
   * campaign goes active again; absent on a campaign the advertiser paused themselves.
   */
  pauseReason: CampaignPauseReasonSchema.optional(),
  /**
   * Cumulative ad spend in integer cents, the figure the budget cap is enforced against.
   * API-only: computed from the backend's billing counter on single-campaign reads, never
   * stored on the campaign document.
   */
  spentCents: z.number().int().nonnegative().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignCreateRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  startDate: z.string().min(1),
  endDate: z.string().min(1),
  globalFrequency: z.number().optional(),
  budget: z.number().optional(), // whole US dollars (see Campaign.budget)
  targetSpecialties: z.array(z.string()).optional(),
});

export type CampaignCreateRequest = z.infer<typeof CampaignCreateRequestSchema>;

export const CampaignUpdateRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  globalFrequency: z.number().optional(),
  budget: z.number().optional(), // whole US dollars (see Campaign.budget)
  targetSpecialties: z.array(z.string()).optional(),
});

export type CampaignUpdateRequest = z.infer<typeof CampaignUpdateRequestSchema>;
